/**
 * Static documentation: the Data Storage Map.
 * Lives in `/app/memory/DATA_STORAGE_MAP.md` for engineers; this page
 * renders the same content in-app so non-engineers can read it too.
 *
 * We import the markdown body as a raw string at build time via
 * raw-loader-equivalent (just hard-code it here since the file rarely
 * changes — and CRA doesn't ship raw-loader by default).
 */
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Database, FileText } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const CONTENT_URL = "/data-storage-map.md";

export default function DataStorageMapPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <div className="border-b border-[#E5E5E5] bg-white px-6 py-3 flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-[#525252] hover:text-[#0055FF]" data-testid="data-map-back">
          <ArrowLeft size={16} /> Back
        </button>
        <a
          href={CONTENT_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-[#525252] hover:text-[#0055FF]"
        >
          Open raw markdown
        </a>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="flex items-center gap-2 mb-6">
          <Database size={24} className="text-[#0055FF]" weight="duotone" />
          <h1 className="text-2xl font-black tracking-tight text-[#0A0A0A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Data Storage Map
          </h1>
        </div>

        <div className="bg-[#EFF6FF] border-l-4 border-[#0055FF] p-4 mb-6">
          <p className="text-sm text-[#0A0A0A] leading-relaxed">
            <strong>TL;DR:</strong> SQL Server is the system of record for every business entity —
            users, clients, projects, tasks, comments, marketing, billing, support, leads, audit
            logs, sessions. MongoDB is used only as a <strong>read-only bridge</strong> to each
            tenant&apos;s own EBA (accounting) Mongo cluster. If you&apos;re wondering &quot;is
            this data in SQL or Mongo?&quot;, the answer is almost certainly <strong>SQL Server</strong>.
          </p>
        </div>

        <div className="bg-white border border-[#E5E5E5] p-6 prose prose-sm max-w-none">
          <div className="flex items-center gap-2 mb-4">
            <FileText size={16} className="text-[#525252]" />
            <span className="text-[11px] font-bold uppercase tracking-wider text-[#525252]">
              The full document is at <code className="text-[#0055FF]">/app/memory/DATA_STORAGE_MAP.md</code>
            </span>
          </div>
          <div className="space-y-4 text-sm text-[#0A0A0A]">
            <h2 className="text-base font-bold">What each backend is for</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>SQL Server</strong> (<code>eyvo_pulse_prod</code>): 108 tables covering auth, multi-tenant, clients, projects, tasks, comments, marketing, billing, quotes, CRM, support, audit, configuration.</li>
              <li><strong>Object Storage</strong> (Emergent-managed S3): every binary file — comment attachments, support ticket files, quote PDFs, lead emails. Metadata row lives in SQL, bytes live here.</li>
              <li><strong>MongoDB</strong>: zero Eyvo Pulse business data. Used only by the EBA bridge (<code>routes/eba_tenant.py</code>) to read each tenant&apos;s own accounting Mongo cluster.</li>
            </ul>

            <h2 className="text-base font-bold pt-4">Where new data goes</h2>
            <table className="w-full text-xs border border-[#E5E5E5]">
              <thead className="bg-[#FAFAFA]">
                <tr>
                  <th className="border border-[#E5E5E5] px-3 py-2 text-left">Kind of data</th>
                  <th className="border border-[#E5E5E5] px-3 py-2 text-left">Storage</th>
                </tr>
              </thead>
              <tbody>
                <tr><td className="border border-[#E5E5E5] px-3 py-1.5">Anything FK-related to an existing entity</td><td className="border border-[#E5E5E5] px-3 py-1.5"><strong>SQL Server</strong></td></tr>
                <tr><td className="border border-[#E5E5E5] px-3 py-1.5">File bytes &gt; 1 KB</td><td className="border border-[#E5E5E5] px-3 py-1.5"><strong>Object Storage</strong> (metadata row in SQL)</td></tr>
                <tr><td className="border border-[#E5E5E5] px-3 py-1.5">Audit / event log</td><td className="border border-[#E5E5E5] px-3 py-1.5"><strong>SQL Server</strong> (<code>audit_logs</code> or domain-specific)</td></tr>
                <tr><td className="border border-[#E5E5E5] px-3 py-1.5">Tenant config / feature flags</td><td className="border border-[#E5E5E5] px-3 py-1.5"><strong>SQL Server</strong> (<code>system_config</code>)</td></tr>
                <tr><td className="border border-[#E5E5E5] px-3 py-1.5">External webhook payloads</td><td className="border border-[#E5E5E5] px-3 py-1.5"><strong>SQL Server</strong></td></tr>
                <tr><td className="border border-[#E5E5E5] px-3 py-1.5">Per-tenant accounting / EBA data</td><td className="border border-[#E5E5E5] px-3 py-1.5"><strong>Tenant&apos;s own Mongo</strong> (read-only)</td></tr>
                <tr className="bg-[#EFF6FF]"><td className="border border-[#E5E5E5] px-3 py-1.5"><em>Anything else</em></td><td className="border border-[#E5E5E5] px-3 py-1.5"><strong>SQL Server.</strong> Default to SQL.</td></tr>
              </tbody>
            </table>

            <h2 className="text-base font-bold pt-4">Verify live</h2>
            <p>
              Open <a className="text-[#0055FF] underline" href="/admin/db-connections">Database Connections</a> from
              the Help menu to see (with passwords masked) which databases this app is actually
              connected to right now.
            </p>

            <h2 className="text-base font-bold pt-4">Why this exists</h2>
            <p>
              Two databases is a common source of confusion (&quot;is the contact in SQL or Mongo?&quot;).
              This map keeps the answer crisp so we don&apos;t accidentally write the same data
              twice or look in the wrong place for it. The full per-table breakdown lives in
              the markdown file referenced above.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
